/**
 * MESSAGE PROVENANCE — where a retrieved claim came from (Phase 4.2, Founder Q5)
 *
 * When Arvexis looked something up, the member can see what she looked at. Nothing appears on
 * an ordinary relational turn, because nothing was retrieved — citations belong to research,
 * not to conversation.
 *
 * Collapsed by default: one quiet line that says a lookup happened and how fresh it is,
 * opening into inspectable sources with their domain and how much weight each carries.
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, ChevronDown, ExternalLink } from 'lucide-react';

const TIER_LABEL = {
  authoritative: { en: 'official source', es: 'fuente oficial' },
  reputable: { en: 'established source', es: 'fuente establecida' },
  community: { en: 'community source', es: 'fuente comunitaria' },
  unknown: { en: 'unverified source', es: 'fuente no verificada' },
};

// The source identity we actually hold: a domain, derived locally from the URL. Presented
// plainly — 'hotels.com' shown as 'Hotels.com'. A host that carries a subdomain is left exactly
// as it is, because capitalising it would read as 'Jp.hotels.com' and because trimming it would
// quietly change which source the member is being told about. No brand name is ever inferred.
const sourceName = (source) => {
  const domain = (source.domain || '').replace(/^www\./, '');
  if (!domain) return source.title || '';
  if (domain.split('.').length > 2) return domain;
  return domain.charAt(0).toUpperCase() + domain.slice(1);
};

export const MessageProvenance = ({ provenance, lang = 'en' }) => {
  const [open, setOpen] = useState(false);
  const sources = provenance?.sources || [];
  if (!sources.length) return null;

  const label = lang === 'es' ? 'Buscado' : 'Looked up';
  const primary = sources[0];
  const extra = sources.length - 1;

  return (
    <div className="mt-2" data-testid="message-provenance">
      {/* One quiet contextual citation for this response, from its own first cited source.
          The complete list stays one tap below; this is the way in. */}
      {primary.url && (
        <a
          href={primary.url}
          target="_blank"
          rel="noreferrer noopener"
          className="inline-flex items-center gap-1.5 mb-1.5 max-w-full text-xs text-white/55 hover:text-white/85 transition-colors"
          data-testid="message-provenance-primary"
        >
          <span className="text-[#D4AF37]/70 leading-none flex-shrink-0">◈</span>
          <span className="truncate underline decoration-[#D4AF37]/25 decoration-1 underline-offset-[3px] hover:decoration-[#D4AF37]/60 transition-colors">
            {sourceName(primary)}
          </span>
          {extra > 0 && <span className="text-white/35 flex-shrink-0">+{extra}</span>}
          <ExternalLink className="w-3 h-3 flex-shrink-0 text-[#D4AF37]/60" />
        </a>
      )}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 text-xs text-white/55 hover:text-white/80 transition-colors"
        data-testid="message-provenance-toggle"
      >
        <Globe className="w-3 h-3" />
        <span>
          {label} · {provenance.freshness} · {sources.length}{' '}
          {sources.length === 1 ? (lang === 'es' ? 'fuente' : 'source') : (lang === 'es' ? 'fuentes' : 'sources')}
          {' · '}
          {lang === 'es' ? 'toca para ver' : 'tap to view'}
        </span>
        <ChevronDown
          className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.ul
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="mt-2 space-y-1.5 overflow-hidden"
            data-testid="message-provenance-sources"
          >
            {sources.map((source, index) => (
              <li key={`${source.url || source.title}-${index}`} className="text-xs">
                <a
                  href={source.url || undefined}
                  target="_blank"
                  rel="noreferrer noopener"
                  className={`flex items-start gap-1.5 ${
                    source.url ? 'text-white/50 hover:text-white/80' : 'text-white/40 pointer-events-none'
                  } transition-colors`}
                  data-testid="message-provenance-source"
                >
                  <span className="flex-1">
                    <span className="block">{source.title}</span>
                    <span className="text-white/30">
                      {source.domain ? `${source.domain} · ` : ''}
                      {(TIER_LABEL[source.tier] || TIER_LABEL.unknown)[lang === 'es' ? 'es' : 'en']}
                    </span>
                  </span>
                  {source.url && <ExternalLink className="w-3 h-3 mt-0.5 flex-shrink-0 opacity-60" />}
                </a>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MessageProvenance;
